/**
 * Formatting helpers for the values BOL2 returns.
 *
 * Balances arrive as pre-formatted strings (PHP `number_format($v, 2, '.', ',')`),
 * so they need parsing before they can be compared or styled.
 */

/** Parses a BOL2 formatted balance such as "1,284.50" or "-320.00". */
export function parseBalance(value: string | null | undefined): number | null {
    if (value === null || value === undefined || value.trim() === '') return null
    const parsed = Number(value.replace(/,/g, ''))
    return Number.isFinite(parsed) ? parsed : null
}

export function formatMoney(amount: number | null | undefined): string {
    if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—'
    return `£${Math.abs(amount).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`
}

export type BalanceStatus = {
    label: string
    tone: 'red' | 'green' | 'grey'
    /** True when money is owed, i.e. the balance is positive. */
    owed: boolean
}

/**
 * A unit's balance is `(demands - receipts) + opening balance`, so a positive figure
 * means money is owed and a negative one means the account is in credit.
 */
export function balanceStatus(amount: number | null): BalanceStatus {
    if (amount === null) return { label: 'Unknown', tone: 'grey', owed: false }
    // Guard against tiny rounding residue showing as arrears.
    if (Math.abs(amount) < 0.005) return { label: 'Up to date', tone: 'green', owed: false }
    if (amount > 0) return { label: 'In arrears', tone: 'red', owed: true }
    return { label: 'In credit', tone: 'green', owed: false }
}

/** Formats an ISO-ish date string as "12 Aug 26". Returns null if unparseable. */
export function formatShortDate(value: string | null | undefined): string | null {
    if (!value) return null
    // BOL2 emits "2026-08-12" and "2026-08-12 09:14:00"; normalise to a form Safari parses.
    const normalised = value.trim().replace(' ', 'T')
    const date = new Date(normalised)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
}

export function formatDateTime(value: Date | null | undefined): string | null {
    if (!value || Number.isNaN(value.getTime())) return null
    return value.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Joins address parts, dropping blanks and de-duplicating repeats. */
export function joinAddress(parts: (string | null | undefined)[]): string {
    const seen = new Set<string>()
    return parts
        .map((part) => part?.trim())
        .filter((part): part is string => Boolean(part))
        .filter((part) => {
            const key = part.toLowerCase()
            if (seen.has(key)) return false
            seen.add(key)
            return true
        })
        .join(', ')
}

export function truncate(value: string, max: number): { text: string; truncated: boolean } {
    if (value.length <= max) return { text: value, truncated: false }
    return { text: `${value.slice(0, max).trimEnd()}…`, truncated: true }
}
